import React, { useState, useEffect } from 'react';
import { ChevronRight, Sparkles, Target, Zap, ArrowRight, Download, FileText } from 'lucide-react';

// Quiz Questions Data
const QUESTIONS = [
  {
    id: 1,
    title: "¿Qué vendes?",
    subtitle: "Selecciona el tipo de producto o servicio que ofreces",
    options: [
      { icon: "🎓", title: "Conocimiento / Formación", desc: "Cursos, mentorías, membresías, formaciones", value: "knowledge" },
      { icon: "💼", title: "Mi tiempo / Consultoría", desc: "Servicios de consultoría, coaching, asesoría", value: "consulting" },
      { icon: "📦", title: "Productos físicos", desc: "Ecommerce, productos tangibles", value: "ecommerce" },
      { icon: "💻", title: "Software / App", desc: "SaaS, aplicaciones, herramientas digitales", value: "software" },
      { icon: "📍", title: "Negocio local", desc: "Servicios presenciales, comercios locales", value: "local" }
    ]
  },
  {
    id: 2,
    title: "¿Cuál es tu ticket medio?",
    subtitle: "El precio promedio de tu producto o servicio principal",
    options: [
      { icon: "💵", title: "Menos de 100€", desc: "Productos de impulso o entrada", value: "low" },
      { icon: "💰", title: "100€ - 500€", desc: "Productos de consideración media", value: "medium" },
      { icon: "💎", title: "500€ - 2.000€", desc: "Productos de alta consideración", value: "high" },
      { icon: "🏆", title: "Más de 2.000€", desc: "High ticket, requiere proceso de venta", value: "premium" }
    ]
  },
  {
    id: 3,
    title: "¿Cómo es tu audiencia actual?",
    subtitle: "El nivel de relación que tienes con tu lista/seguidores",
    options: [
      { icon: "🌱", title: "Estoy empezando", desc: "Menos de 1.000 contactos/seguidores", value: "starting" },
      { icon: "🌿", title: "Audiencia pequeña", desc: "1.000 - 5.000 contactos activos", value: "small" },
      { icon: "🌳", title: "Audiencia media", desc: "5.000 - 20.000 contactos activos", value: "medium" },
      { icon: "🏔️", title: "Audiencia grande", desc: "Más de 20.000 contactos activos", value: "large" }
    ]
  },
  {
    id: 4,
    title: "¿Qué tiempo tienes para preparar?",
    subtitle: "Tiempo hasta tu próximo lanzamiento o campaña",
    options: [
      { icon: "⚡", title: "Necesito vender YA", desc: "Esta semana o la próxima", value: "urgent" },
      { icon: "📅", title: "2-4 semanas", desc: "Tengo algo de tiempo para preparar", value: "short" },
      { icon: "🗓️", title: "1-2 meses", desc: "Puedo planificar bien la estrategia", value: "medium" },
      { icon: "🎯", title: "Más de 2 meses", desc: "Quiero hacer algo grande y bien preparado", value: "long" }
    ]
  },
  {
    id: 5,
    title: "¿Qué prefieres?",
    subtitle: "Tu estilo de comunicación con tu audiencia",
    options: [
      { icon: "🎥", title: "Directos / Webinars", desc: "Me gusta conectar en vivo con mi audiencia", value: "live" },
      { icon: "📹", title: "Vídeos pregrabados", desc: "Prefiero grabar y editar mi contenido", value: "recorded" },
      { icon: "✍️", title: "Texto / Emails", desc: "Mi fuerte es escribir, no aparecer en cámara", value: "text" },
      { icon: "💬", title: "WhatsApp / Mensajes", desc: "Prefiero comunicación directa y personal", value: "messaging" }
    ]
  },
  {
    id: 6,
    title: "¿Cuál es tu objetivo principal?",
    subtitle: "Lo que más te importa conseguir ahora",
    options: [
      { icon: "💸", title: "Generar ingresos rápido", desc: "Necesito facturar cuanto antes", value: "revenue" },
      { icon: "📈", title: "Crecer mi lista", desc: "Quiero más leads y suscriptores", value: "leads" },
      { icon: "🏗️", title: "Construir autoridad", desc: "Posicionarme como referente en mi sector", value: "authority" },
      { icon: "🔄", title: "Automatizar ventas", desc: "Que el negocio venda sin mi presencia", value: "automation" }
    ]
  },
  {
    id: 7,
    title: "¿Qué recursos tienes?",
    subtitle: "Herramientas y equipo disponible",
    options: [
      { icon: "🙋", title: "Solo yo", desc: "Hago todo yo mismo/a", value: "solo" },
      { icon: "👥", title: "Equipo pequeño", desc: "1-3 personas que me ayudan", value: "small_team" },
      { icon: "🏢", title: "Equipo completo", desc: "Tengo equipo de marketing, diseño, etc.", value: "full_team" },
      { icon: "🤝", title: "Agencia externa", desc: "Trabajo con agencia de marketing", value: "agency" }
    ]
  }
];

// Strategy Results Logic with detailed implementation guide
const STRATEGY_RESULTS = {
  // Knowledge + High ticket + Live = PLF or Launch
  "knowledge_premium_live": {
    strategy: "PLF Clásico 4 Vídeos",
    icon: "📦",
    category: "PLF",
    description: "Tu perfil es ideal para un Product Launch Formula completo. Con tu ticket alto y preferencia por directos, puedes construir máxima autoridad con una serie de vídeos de alto valor.",
    benefits: ["Máxima autoridad y posicionamiento", "Justifica tickets altos", "Crea expectativa y urgencia real"],
    assets: { landings: 6, emails: 22, whatsapp: 10, creativos: 18 },
    guide: {
      overview: "El PLF (Product Launch Formula) es una estrategia de 4 vídeos que educan y construyen deseo antes de abrir carrito. Ideal para productos de alto valor donde necesitas justificar el precio.",
      timeline: "3-4 semanas de preparación + 2 semanas de lanzamiento",
      steps: [
        { title: "Semana 1-2: Preparación", tasks: ["Graba los 4 vídeos de contenido", "Crea las 6 landing pages", "Configura secuencias de email", "Prepara creativos de ads"] },
        { title: "Semana 3: Pre-lanzamiento", tasks: ["Abre lista de espera", "Lanza ads a audiencia fría", "Envía emails de anticipación", "Calienta en redes sociales"] },
        { title: "Semana 4: Vídeo 1 - Oportunidad", tasks: ["Libera primer vídeo", "Email de aviso + recordatorio", "Ads de retargeting", "WhatsApp a lista VIP"] },
        { title: "Semana 5: Vídeos 2-3", tasks: ["Vídeo 2: Transformación", "Vídeo 3: Experiencia/Caso de éxito", "Secuencia de emails de nurturing", "Engagement en comentarios"] },
        { title: "Semana 6: Vídeo 4 + Carrito", tasks: ["Vídeo 4: La Oferta", "Abre carrito por 5-7 días", "Secuencia de cierre (escasez)", "Bonus early bird"] }
      ],
      tips: ["Cada vídeo debe aportar valor real, no solo vender", "Usa testimonios en vídeos 2 y 3", "El vídeo 4 debe tener una oferta irresistible con bonos", "Deadline real = urgencia real"]
    }
  },
  "knowledge_high_live": {
    strategy: "Lanzamiento 2 Directos",
    icon: "🔴",
    category: "Lanzamiento",
    description: "Perfecto para ti: un webinar de contenido seguido de un directo de cierre. Tiempo de nurturing ideal para tu ticket medio-alto.",
    benefits: ["Conecta en vivo con tu audiencia", "Más tiempo para generar confianza", "Carrito de 48h con urgencia real"],
    assets: { landings: 6, emails: 15, whatsapp: 8, creativos: 14 },
    guide: {
      overview: "Dos directos espaciados 3-4 días: el primero educa y el segundo cierra. Permite más tiempo de reflexión que un solo webinar.",
      timeline: "2 semanas de preparación + 1 semana de lanzamiento",
      steps: [
        { title: "Semana 1: Preparación", tasks: ["Crea la presentación del Directo 1 (educativo)", "Crea la presentación del Directo 2 (cierre)", "Diseña landing de registro", "Configura emails de confirmación y recordatorio"] },
        { title: "Semana 2: Captación", tasks: ["Lanza ads a landing de registro", "Emails a tu lista existente", "Posts orgánicos en redes", "WhatsApp a contactos calientes"] },
        { title: "Día del Directo 1", tasks: ["Masterclass de contenido puro (60-90 min)", "Al final: anticipa el Directo 2", "NO vendes aquí, solo educas", "Envía replay a no asistentes"] },
        { title: "Entre directos (3-4 días)", tasks: ["Email con resumen del Directo 1", "Testimonios y casos de éxito", "Anticipa lo que viene en Directo 2", "WhatsApp de recordatorio"] },
        { title: "Directo 2 + Carrito", tasks: ["Repasa puntos clave del Directo 1", "Presenta la oferta completa", "Abre carrito 24-48h", "Secuencia de cierre con urgencia"] }
      ],
      tips: ["El Directo 1 debe ser tu mejor contenido gratuito", "Entre directos mantén el engagement alto", "El Directo 2 puede ser más corto (45-60 min)", "Usa countdown en emails de cierre"]
    }
  },
  "knowledge_medium_live": {
    strategy: "Lanzamiento 1 Solo Directo",
    icon: "🔴",
    category: "Lanzamiento",
    description: "Un webinar potente que educa y vende. Simple, efectivo y perfecto para tu nivel de ticket.",
    benefits: ["Rápido de preparar", "Alta conversión si ejecutas bien", "Urgencia de carrito 24h"],
    assets: { landings: 6, emails: 8, whatsapp: 6, creativos: 11 },
    guide: {
      overview: "El clásico webinar de lanzamiento: captas registros, das contenido de valor y presentas tu oferta al final con carrito abierto por tiempo limitado.",
      timeline: "1-2 semanas de preparación + 3-4 días de lanzamiento",
      steps: [
        { title: "Semana 1: Preparación", tasks: ["Crea tu presentación (estructura: Hook → Contenido → Pitch)", "Diseña landing de registro con countdown", "Configura emails: confirmación, recordatorios, replay", "Prepara página de ventas"] },
        { title: "Días 1-7: Captación", tasks: ["Lanza ads a landing de registro", "Email a tu lista: 'Reserva tu plaza'", "Posts orgánicos con teaser del contenido", "WhatsApp a contactos más calientes"] },
        { title: "Día del Webinar", tasks: ["Envía recordatorio 1h antes", "Da 50-60 min de contenido puro", "Últimos 20-30 min: presenta oferta", "Abre carrito con bonus de acción rápida"] },
        { title: "Post-Webinar (24-48h)", tasks: ["Envía replay a no asistentes", "Email recordando que carrito cierra", "WhatsApp de última oportunidad", "Cierra carrito a la hora exacta"] }
      ],
      tips: ["Los primeros 5 minutos son cruciales - engancha rápido", "No vendas durante el contenido, solo al final", "El replay puede generar 30-40% de las ventas", "Deadline real = no reabras el carrito"]
    }
  },
  "knowledge_automation": {
    strategy: "Webinar Automatizado",
    icon: "🌿",
    category: "Evergreen",
    description: "Graba tu mejor webinar una vez y véndelo 24/7 con deadline automatizado.",
    benefits: ["Vende mientras duermes", "Escala sin límite", "Optimizable con datos reales"],
    assets: { landings: 5, emails: 10, whatsapp: 5, creativos: 5 },
    guide: {
      overview: "Webinar pregrabado que simula ser en vivo. El usuario elige su horario, ve el webinar y tiene un deadline personal para comprar.",
      timeline: "2-3 semanas de setup inicial, luego funciona en automático",
      steps: [
        { title: "Fase 1: Grabación", tasks: ["Graba tu mejor webinar (puede ser uno que ya hiciste)", "Edita para quitar referencias a fechas específicas", "Añade chat simulado o interacciones pregrabadas", "Duración ideal: 45-75 minutos"] },
        { title: "Fase 2: Setup Técnico", tasks: ["Configura plataforma de webinar automatizado", "Crea landing con selector de horarios", "Configura deadline dinámico (48-72h post-webinar)", "Integra con tu email marketing"] },
        { title: "Fase 3: Secuencias", tasks: ["Email de confirmación + acceso", "Recordatorios pre-webinar (1h, 15min)", "Email post-webinar con oferta", "Secuencia de cierre antes del deadline"] },
        { title: "Fase 4: Tráfico", tasks: ["Ads evergreen a audiencia fría", "Retargeting a visitantes de landing", "Email a nuevos suscriptores", "Optimiza según datos de conversión"] }
      ],
      tips: ["El webinar debe parecer en vivo pero sin mentir", "Ofrece Q&A por email o chat post-webinar", "Testea diferentes horarios para optimizar", "El deadline personal crea urgencia real"]
    }
  },
  "knowledge_text": {
    strategy: "VSL Funnel",
    icon: "🌿",
    category: "Evergreen",
    description: "Si prefieres no aparecer en cámara, un VSL con texto en pantalla puede funcionar muy bien.",
    benefits: ["No requiere aparecer en cámara", "Automatizable 100%", "Simple y efectivo"],
    assets: { landings: 4, emails: 7, whatsapp: 3, creativos: 6 },
    guide: {
      overview: "Video Sales Letter: un vídeo de 15-45 minutos con slides/texto que vende tu producto. El visitante llega, ve el vídeo y compra (o no).",
      timeline: "1-2 semanas de creación + optimización continua",
      steps: [
        { title: "Fase 1: Script", tasks: ["Escribe el guión siguiendo fórmula PAS o AIDA", "Hook potente en los primeros 30 segundos", "Presenta el problema y agita", "Muestra la solución (tu producto)"] },
        { title: "Fase 2: Producción", tasks: ["Graba el audio (puedes usar voz IA)", "Crea slides minimalistas o usa B-roll", "Edita con ritmo dinámico", "Añade testimonios en texto/vídeo"] },
        { title: "Fase 3: Landing", tasks: ["Crea landing con VSL arriba del fold", "Botón de compra que aparece tras X minutos", "Testimonios y FAQs debajo", "Página de checkout simple"] },
        { title: "Fase 4: Tráfico", tasks: ["Ads directos a la VSL", "Retargeting a quienes vieron >50%", "Email a leads que no compraron", "Testea diferentes hooks de ads"] }
      ],
      tips: ["Los primeros 3 minutos definen si siguen viendo", "Incluye prueba social cada 5-7 minutos", "Ofrece garantía para reducir fricción", "Testea versiones cortas vs largas del VSL"]
    }
  },
  "consulting_premium": {
    strategy: "Aplicación + Llamada",
    icon: "📞",
    category: "High Ticket",
    description: "Para servicios de alto valor, necesitas un proceso de cualificación. VSL o caso de estudio + formulario de aplicación + llamada de cierre.",
    benefits: ["Filtra leads no cualificados", "Cierra en llamada 1:1", "Justifica tu precio premium"],
    assets: { landings: 4, emails: 8, whatsapp: 4, creativos: 6 },
    guide: {
      overview: "Funnel de alto ticket: el prospecto ve contenido de valor, aplica rellenando un formulario, y si cualifica, agenda llamada contigo para cerrar.",
      timeline: "1-2 semanas de setup + proceso continuo",
      steps: [
        { title: "Fase 1: Contenido de entrada", tasks: ["Crea VSL o caso de estudio en vídeo (20-40 min)", "Muestra transformación de clientes", "Posiciona tu metodología única", "CTA: 'Aplica para trabajar conmigo'"] },
        { title: "Fase 2: Aplicación", tasks: ["Formulario de 8-12 preguntas cualificadoras", "Pregunta sobre presupuesto disponible", "Pregunta sobre nivel de compromiso", "Recoge email y teléfono"] },
        { title: "Fase 3: Cualificación", tasks: ["Revisa aplicaciones manualmente o con scoring", "Descarta no cualificados con email educado", "A cualificados: email/WhatsApp para agendar", "Usa Calendly o similar para la agenda"] },
        { title: "Fase 4: Llamada de cierre", tasks: ["Estructura: Situación actual → Situación deseada → Obstáculos → Tu solución", "Presenta oferta clara con precio", "Maneja objeciones comunes", "Cierra o agenda seguimiento"] }
      ],
      tips: ["La aplicación es un filtro, no una barrera", "Responde rápido a aplicaciones cualificadas", "En la llamada, pregunta más de lo que hablas", "Graba llamadas para mejorar tu proceso"]
    }
  },
  "consulting_medium": {
    strategy: "Micro-Webinar + Llamada",
    icon: "📞",
    category: "High Ticket",
    description: "Un webinar corto de 20-30 min que precalienta y al final invita a agendar llamada.",
    benefits: ["Más personal que un VSL largo", "Pre-cualifica interesados", "Demuestra tu expertise rápido"],
    assets: { landings: 3, emails: 6, whatsapp: 4, creativos: 5 },
    guide: {
      overview: "Webinar express que va al grano: 20-30 minutos de contenido potente y al final invitas a agendar llamada de estrategia gratuita.",
      timeline: "1 semana de preparación + proceso continuo",
      steps: [
        { title: "Fase 1: Micro-Webinar", tasks: ["Crea presentación de 20-30 minutos máximo", "Estructura: 1 gran insight + 3 claves + CTA", "Graba en buena calidad (puede ser Loom)", "CTA final: 'Agenda tu sesión estratégica'"] },
        { title: "Fase 2: Funnel", tasks: ["Landing de registro sencilla", "Página de thank you con acceso al vídeo", "Debajo del vídeo: botón de agendar", "Emails de recordatorio y seguimiento"] },
        { title: "Fase 3: Captación", tasks: ["Ads a audiencias tibias/calientes", "Email a tu lista existente", "LinkedIn si es B2B", "Contenido orgánico que lleva al webinar"] },
        { title: "Fase 4: Llamadas", tasks: ["Sesión estratégica de 30-45 min", "Primero diagnóstico, luego tu solución", "Presenta oferta si hay fit", "Seguimiento a los que no cierran"] }
      ],
      tips: ["El micro-webinar debe ser TU mejor contenido comprimido", "No vendas en el webinar, vende en la llamada", "La sesión estratégica debe aportar valor real", "Ten claro tu precio antes de la llamada"]
    }
  },
  "ecommerce_urgent": {
    strategy: "Flash Sale 48h",
    icon: "🛒",
    category: "Ecommerce",
    description: "Venta relámpago con cuenta regresiva. Inyecta liquidez rápido con urgencia real.",
    benefits: ["Resultados en 48h", "Urgencia máxima", "Mueve inventario rápido"],
    assets: { landings: 3, emails: 6, whatsapp: 4, creativos: 8 },
    guide: {
      overview: "Promoción de 48 horas con descuento agresivo. Ideal para generar caja rápido, liquidar stock o reactivar clientes dormidos.",
      timeline: "2-3 días de preparación + 48h de venta",
      steps: [
        { title: "Preparación (Día -3 a -1)", tasks: ["Define el descuento (mínimo 20-30% para que impacte)", "Crea landing con countdown real", "Prepara creativos de ads", "Segmenta tu lista: clientes, leads, abandonos"] },
        { title: "Día 0: Lanzamiento", tasks: ["Email de apertura a toda la lista", "Activa ads con urgencia", "WhatsApp a mejores clientes", "Post en redes con el countdown"] },
        { title: "Día 1: Mantenimiento", tasks: ["Email recordatorio 'Quedan 24h'", "Retargeting a visitantes de landing", "Stories con productos destacados", "Responde dudas rápido"] },
        { title: "Día 2: Cierre", tasks: ["Email 'Últimas 12 horas'", "Email 'Última hora' - cierre inminente", "WhatsApp de cierre", "Cierra puntualmente - no extiendas"] }
      ],
      tips: ["Descuento real, no inflado artificialmente", "El countdown debe ser real (no fake)", "Usa prueba social: 'X personas ya compraron'", "Después del cierre, no reabras - protege tu credibilidad"]
    }
  },
  "ecommerce_standard": {
    strategy: "Venta Directa / Producto Estrella",
    icon: "🛒",
    category: "Ecommerce",
    description: "Tráfico directo a tu producto ganador optimizado para conversión inmediata.",
    benefits: ["ROAS medible desde día 1", "Simple de implementar", "Escalable con paid ads"],
    assets: { landings: 2, emails: 5, whatsapp: 3, creativos: 6 },
    guide: {
      overview: "El funnel más directo: ads → página de producto → compra. Funciona cuando tienes un producto probado con buenas fotos y reviews.",
      timeline: "Setup inicial de 1 semana + optimización continua",
      steps: [
        { title: "Fase 1: Producto estrella", tasks: ["Identifica tu producto con mejor margen y demanda", "Optimiza fotos (fondo blanco + lifestyle)", "Recopila y muestra reviews reales", "Escribe descripción con beneficios, no features"] },
        { title: "Fase 2: Página de producto", tasks: ["Galería de fotos de alta calidad", "Título con beneficio principal", "Precio claro (con descuento si aplica)", "Reviews visibles arriba del fold"] },
        { title: "Fase 3: Checkout", tasks: ["Checkout de 1 página máximo", "Ofrece envío gratis si es posible", "Muestra sellos de seguridad", "Upsell/cross-sell en checkout"] },
        { title: "Fase 4: Ads + Email", tasks: ["Ads a audiencias lookalike de compradores", "Retargeting a visitantes y abandonos", "Email de carrito abandonado (3 emails)", "Email post-compra pidiendo review"] }
      ],
      tips: ["El producto debe tener fotos profesionales", "Los reviews son tu mejor vendedor", "Testea diferentes hooks en ads", "El abandono de carrito es tu mina de oro"]
    }
  },
  "local_business": {
    strategy: "Oferta Gancho Local",
    icon: "📍",
    category: "Negocios Locales",
    description: "Oferta irresistible para primera visita. Objetivo: agendar cita o visita.",
    benefits: ["Atrae clientes nuevos", "Fácil de medir ROI", "Google Ads + Landing optimizada"],
    assets: { landings: 2, emails: 4, whatsapp: 3, creativos: 4 },
    guide: {
      overview: "Captas leads locales con una oferta gancho (primera sesión gratis, descuento primera visita, etc.) y los conviertes en clientes recurrentes.",
      timeline: "1 semana de setup + campaña continua",
      steps: [
        { title: "Fase 1: Oferta gancho", tasks: ["Define una oferta irresistible de entrada", "Ejemplos: Primera consulta gratis, 50% primera sesión, Diagnóstico gratuito", "Calcula el coste de adquisición que puedes permitirte", "La oferta debe llevar a tu servicio principal"] },
        { title: "Fase 2: Landing local", tasks: ["Diseño simple con tu oferta destacada", "Incluye dirección y mapa", "Fotos reales de tu negocio", "Formulario corto: nombre, teléfono, horario preferido"] },
        { title: "Fase 3: Google Ads", tasks: ["Campaña de búsqueda local ('dentista en [ciudad]')", "Extensiones de ubicación y llamada", "Keywords de intención: 'cerca de mí', 'urgente', 'hoy'", "Landing específica por servicio si tienes varios"] },
        { title: "Fase 4: Seguimiento", tasks: ["Llamada de confirmación en menos de 2h", "WhatsApp de recordatorio día antes", "Email de bienvenida con qué esperar", "Post-visita: pide reseña en Google"] }
      ],
      tips: ["Responde leads en menos de 5 minutos si puedes", "Las reseñas en Google son tu mejor inversión", "Mide el valor del cliente a largo plazo, no solo la primera venta", "Ofrece algo de valor real, no un descuento ridículo"]
    }
  },
  "messaging_preferred": {
    strategy: "Lanzamiento Meteorito (WhatsApp)",
    icon: "☄️",
    category: "WhatsApp",
    description: "Grupos de WhatsApp con contenido intensivo de 3-5 días y oferta relámpago al final.",
    benefits: ["Conexión directa y personal", "Altas tasas de apertura", "Urgencia de grupo"],
    assets: { landings: 3, emails: 4, whatsapp: 8, creativos: 6 },
    guide: {
      overview: "Creas un grupo de WhatsApp (o Telegram) donde das contenido intensivo durante 3-5 días y al final lanzas una oferta por tiempo muy limitado.",
      timeline: "1 semana de captación + 3-5 días de lanzamiento",
      steps: [
        { title: "Fase 1: Captación al grupo", tasks: ["Landing con propuesta de valor clara", "Promete: 'X días de contenido exclusivo + oferta especial'", "Pide número de WhatsApp para unir al grupo", "Email y ads dirigiendo al grupo"] },
        { title: "Fase 2: Días de contenido", tasks: ["Día 1: Contexto y gran promesa", "Día 2-3: Contenido de alto valor (audios, PDFs, vídeos cortos)", "Día 4: Anticipa la oferta que viene", "Mantén engagement con preguntas al grupo"] },
        { title: "Fase 3: Oferta meteorito", tasks: ["Día 5: Lanza la oferta en el grupo", "Oferta disponible solo 24-48h", "Bonos exclusivos para el grupo", "Usa urgencia real: plazas limitadas o precio especial"] },
        { title: "Fase 4: Cierre", tasks: ["Recordatorios de cierre en el grupo", "Testimonios de primeros compradores", "Cierra el carrito públicamente", "Agradece y cierra el grupo"] }
      ],
      tips: ["Máximo 200-500 personas por grupo para que sea manejable", "El contenido debe ser realmente bueno, no un teaser", "La oferta debe ser exclusiva para el grupo", "Después del lanzamiento, puedes crear comunidad de compradores"]
    }
  },
  "leads_focused": {
    strategy: "Quiz Funnel",
    icon: "🌿",
    category: "Evergreen",
    description: "Cuestionario de segmentación que da resultado personalizado. Ideal para crecer lista y segmentar.",
    benefits: ["Alto ratio de opt-in", "Segmenta automáticamente", "Personaliza la oferta por perfil"],
    assets: { landings: 5, emails: 8, whatsapp: 4, creativos: 6 },
    guide: {
      overview: "Un quiz interactivo que clasifica al usuario y le da un resultado personalizado. Captas el email para enviar el resultado y luego nutres con contenido relevante.",
      timeline: "2 semanas de setup + optimización continua",
      steps: [
        { title: "Fase 1: Diseño del quiz", tasks: ["Define 5-10 preguntas que segmenten bien", "Crea 3-5 perfiles de resultado posibles", "Cada resultado debe tener su oferta asociada", "El quiz debe ser rápido (2-3 minutos máximo)"] },
        { title: "Fase 2: Resultados", tasks: ["Página de resultado personalizada por perfil", "Explica qué significa su resultado", "Ofrece recurso gratuito relacionado", "CTA hacia tu oferta específica para ese perfil"] },
        { title: "Fase 3: Secuencias", tasks: ["Email inmediato con su resultado completo", "Secuencia de nurturing según perfil", "Contenido relevante para su situación", "Oferta específica tras 3-5 emails de valor"] },
        { title: "Fase 4: Tráfico", tasks: ["Ads con curiosidad: '¿Cuál es tu tipo de X?'", "El quiz convierte mejor que un lead magnet normal", "Testea diferentes preguntas iniciales", "Retargeting a quienes no completaron"] }
      ],
      tips: ["El quiz debe ser entretenido, no un examen", "El resultado debe hacer sentir comprendido al usuario", "Personaliza TODO lo posible según el perfil", "Usa el quiz para aprender sobre tu audiencia"]
    }
  },
  "authority_building": {
    strategy: "Reto 5 Días",
    icon: "🏆",
    category: "Desafío",
    description: "Desafío de transformación que genera resultados rápidos y construye comunidad.",
    benefits: ["Crea tribu y comunidad", "Demuestra tu método funciona", "Alta conversión al final"],
    assets: { landings: 4, emails: 12, whatsapp: 8, creativos: 10 },
    guide: {
      overview: "Un reto de 5 días donde los participantes implementan algo concreto cada día. Al final, ofreces tu programa completo para continuar la transformación.",
      timeline: "2 semanas de preparación + 1 semana de reto + cierre",
      steps: [
        { title: "Preparación", tasks: ["Define la transformación pequeña pero real de los 5 días", "Estructura: Día 1 mindset, Días 2-4 acción, Día 5 integración + oferta", "Crea grupo de Facebook o comunidad", "Prepara emails y contenido de cada día"] },
        { title: "Captación (7-14 días antes)", tasks: ["Landing del reto con fecha de inicio", "Promesa clara: 'En 5 días lograrás X'", "Ads, email a lista, posts orgánicos", "Crea expectativa con countdown"] },
        { title: "Durante el reto", tasks: ["Email + contenido diario (vídeo corto o live)", "Tarea accionable cada día", "Engagement en el grupo: celebra wins", "Día 4-5: anticipa la oferta"] },
        { title: "Cierre", tasks: ["Día 5 o 6: presenta tu programa completo", "Oferta especial solo para participantes del reto", "Carrito abierto 48-72h", "Testimonios de participantes que tuvieron resultados"] }
      ],
      tips: ["Las tareas deben ser pequeñas pero con resultado visible", "Celebra cada pequeña victoria en el grupo", "Los que completan el reto son tus mejores prospectos", "El reto es un 'taste' de trabajar contigo"]
    }
  },
  "default": {
    strategy: "Lanzamiento 1 Solo Directo",
    icon: "🔴",
    category: "Lanzamiento",
    description: "El formato más versátil y probado. Un webinar que educa, conecta y vende.",
    benefits: ["Funciona para casi cualquier nicho", "Rápido de implementar", "Alta conversión si ejecutas bien"],
    assets: { landings: 6, emails: 8, whatsapp: 6, creativos: 11 },
    guide: {
      overview: "El clásico webinar de lanzamiento: captas registros, das contenido de valor y presentas tu oferta al final con carrito abierto por tiempo limitado.",
      timeline: "1-2 semanas de preparación + 3-4 días de lanzamiento",
      steps: [
        { title: "Semana 1: Preparación", tasks: ["Crea tu presentación (estructura: Hook → Contenido → Pitch)", "Diseña landing de registro con countdown", "Configura emails: confirmación, recordatorios, replay", "Prepara página de ventas"] },
        { title: "Días 1-7: Captación", tasks: ["Lanza ads a landing de registro", "Email a tu lista: 'Reserva tu plaza'", "Posts orgánicos con teaser del contenido", "WhatsApp a contactos más calientes"] },
        { title: "Día del Webinar", tasks: ["Envía recordatorio 1h antes", "Da 50-60 min de contenido puro", "Últimos 20-30 min: presenta oferta", "Abre carrito con bonus de acción rápida"] },
        { title: "Post-Webinar (24-48h)", tasks: ["Envía replay a no asistentes", "Email recordando que carrito cierra", "WhatsApp de última oportunidad", "Cierra carrito a la hora exacta"] }
      ],
      tips: ["Los primeros 5 minutos son cruciales - engancha rápido", "No vendas durante el contenido, solo al final", "El replay puede generar 30-40% de las ventas", "Deadline real = no reabras el carrito"]
    }
  }
};

// Determine strategy based on answers
function determineStrategy(answers) {
  const product = answers[0];
  const ticket = answers[1];
  const audience = answers[2];
  const timeline = answers[3];
  const style = answers[4];
  const goal = answers[5];
  const resources = answers[6];

  // Local business
  if (product === 'local') return STRATEGY_RESULTS.local_business;
  
  // Ecommerce
  if (product === 'ecommerce') {
    if (timeline === 'urgent') return STRATEGY_RESULTS.ecommerce_urgent;
    return STRATEGY_RESULTS.ecommerce_standard;
  }
  
  // Consulting high ticket
  if (product === 'consulting' && (ticket === 'premium' || ticket === 'high')) {
    if (style === 'live') return STRATEGY_RESULTS.consulting_medium;
    return STRATEGY_RESULTS.consulting_premium;
  }
  
  // Messaging preference
  if (style === 'messaging') return STRATEGY_RESULTS.messaging_preferred;
  
  // Leads focused goal
  if (goal === 'leads') return STRATEGY_RESULTS.leads_focused;
  
  // Authority building
  if (goal === 'authority') return STRATEGY_RESULTS.authority_building;
  
  // Automation goal
  if (goal === 'automation') return STRATEGY_RESULTS.knowledge_automation;
  
  // Text preference (no camera)
  if (style === 'text') return STRATEGY_RESULTS.knowledge_text;
  
  // Knowledge/Software with live preference
  if ((product === 'knowledge' || product === 'software') && style === 'live') {
    if (ticket === 'premium') return STRATEGY_RESULTS["knowledge_premium_live"];
    if (ticket === 'high') return STRATEGY_RESULTS["knowledge_high_live"];
    return STRATEGY_RESULTS["knowledge_medium_live"];
  }
  
  // Default
  return STRATEGY_RESULTS.default;
}

const QuizLanding = () => {
  const [started, setStarted] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [pendingResult, setPendingResult] = useState(null);

  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;

  // Load GHL form script
  useEffect(() => {
    if (showLeadCapture) {
      const existingScript = document.querySelector('script[src="https://link.msgsndr.com/js/form_embed.js"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://link.msgsndr.com/js/form_embed.js';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [showLeadCapture]);

  const handleOptionClick = (value) => {
    setIsAnimating(true);
    const newAnswers = [...answers, value];
    setAnswers(newAnswers);

    setTimeout(() => {
      if (currentQuestion < QUESTIONS.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Calculate result and show lead capture form
        const strategy = determineStrategy(newAnswers);
        setPendingResult(strategy);
        setShowLeadCapture(true);
      }
      setIsAnimating(false);
    }, 300);
  };

  const handleShowResult = () => {
    setResult(pendingResult);
    setShowLeadCapture(false);
  };

  const handleRestart = () => {
    setStarted(false);
    setCurrentQuestion(0);
    setAnswers([]);
    setResult(null);
    setShowLeadCapture(false);
    setPendingResult(null);
  };

  // Generate and download PDF with strategy guide
  const handleDownloadPDF = () => {
    if (!result) return;
    
    const guide = result.guide || {};
    const pdfContent = `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Tu Estrategia: ${result.strategy} - FASCINAI ONE</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
    
    :root {
      --primary: #4E62F4;
      --primary-light: #637CE2;
      --accent: #C6FD38;
      --purple: #C28CFA;
      --lavender: #95A0F0;
      --dark: #0a0a12;
      --dark-card: #12121c;
      --text: #ffffff;
      --text-muted: rgba(255,255,255,0.6);
    }
    
    * { margin: 0; padding: 0; box-sizing: border-box; }
    
    body { 
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; 
      background: linear-gradient(180deg, var(--dark) 0%, #0d0d18 100%);
      color: var(--text); 
      line-height: 1.7;
      min-height: 100vh;
    }
    
    .page { padding: 50px; max-width: 900px; margin: 0 auto; }
    
    /* Header */
    .header { 
      text-align: center; 
      margin-bottom: 50px; 
      padding: 40px;
      background: linear-gradient(135deg, rgba(78, 98, 244, 0.15) 0%, rgba(194, 140, 250, 0.1) 100%);
      border-radius: 24px;
      border: 1px solid rgba(78, 98, 244, 0.2);
      position: relative;
      overflow: hidden;
    }
    
    .header::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -20%;
      width: 300px;
      height: 300px;
      background: radial-gradient(circle, rgba(198, 253, 56, 0.1) 0%, transparent 70%);
      border-radius: 50%;
    }
    
    .logo { 
      margin-bottom: 24px;
      text-align: center;
    }
    
    .logo img {
      height: 50px;
      width: auto;
      object-fit: contain;
    }
    
    .badge { 
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px; 
      background: rgba(198, 253, 56, 0.15); 
      border: 1px solid rgba(198, 253, 56, 0.3); 
      border-radius: 30px; 
      font-size: 13px; 
      font-weight: 600;
      color: var(--accent); 
      margin-bottom: 24px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    
    .strategy-icon { font-size: 72px; margin-bottom: 16px; filter: drop-shadow(0 4px 20px rgba(78, 98, 244, 0.3)); }
    
    .strategy-category { 
      color: var(--lavender); 
      font-size: 13px; 
      font-weight: 700; 
      text-transform: uppercase; 
      letter-spacing: 2px;
      margin-bottom: 8px;
    }
    
    .strategy-name { 
      font-size: 42px; 
      font-weight: 800; 
      margin: 12px 0 20px;
      background: linear-gradient(90deg, #fff 0%, var(--lavender) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      line-height: 1.2;
    }
    
    .strategy-desc { 
      font-size: 18px; 
      color: var(--text-muted); 
      max-width: 650px; 
      margin: 0 auto;
      line-height: 1.8;
    }
    
    /* Sections */
    .section { margin: 40px 0; }
    
    .section-title { 
      font-size: 22px; 
      font-weight: 700; 
      color: var(--text);
      margin-bottom: 24px; 
      padding-bottom: 12px; 
      border-bottom: 2px solid rgba(78, 98, 244, 0.3);
      display: flex;
      align-items: center;
      gap: 12px;
    }
    
    .section-title span { font-size: 24px; }
    
    /* Benefits */
    .benefits { display: flex; flex-direction: column; gap: 12px; }
    
    .benefit { 
      display: flex; 
      align-items: center; 
      gap: 16px; 
      padding: 16px 20px; 
      background: var(--dark-card);
      border-radius: 14px;
      border: 1px solid rgba(255,255,255,0.06);
      transition: all 0.2s;
    }
    
    .benefit-icon { 
      width: 32px; 
      height: 32px; 
      background: linear-gradient(135deg, rgba(198, 253, 56, 0.2) 0%, rgba(198, 253, 56, 0.1) 100%);
      border-radius: 10px; 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      color: var(--accent); 
      font-size: 16px;
      font-weight: bold;
    }
    
    .benefit span { font-size: 16px; color: var(--text); font-weight: 500; }
    
    /* Assets Grid */
    .assets-grid { 
      display: grid; 
      grid-template-columns: repeat(4, 1fr); 
      gap: 16px; 
    }
    
    .asset { 
      text-align: center; 
      padding: 28px 20px;
      background: linear-gradient(180deg, var(--dark-card) 0%, rgba(18, 18, 28, 0.8) 100%);
      border-radius: 16px;
      border: 1px solid rgba(78, 98, 244, 0.2);
    }
    
    .asset-number { 
      font-size: 40px; 
      font-weight: 800; 
      background: linear-gradient(180deg, var(--primary) 0%, var(--purple) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .asset-label { 
      font-size: 13px; 
      color: var(--text-muted); 
      margin-top: 8px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    /* Overview */
    .overview { 
      padding: 28px;
      background: linear-gradient(135deg, rgba(78, 98, 244, 0.12) 0%, rgba(194, 140, 250, 0.08) 100%);
      border: 1px solid rgba(78, 98, 244, 0.25);
      border-radius: 18px;
      margin-bottom: 32px;
    }
    
    .overview-text { font-size: 17px; color: rgba(255,255,255,0.85); line-height: 1.8; }
    
    .timeline { 
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 10px 18px;
      background: linear-gradient(90deg, rgba(198, 253, 56, 0.15) 0%, rgba(198, 253, 56, 0.08) 100%);
      border: 1px solid rgba(198, 253, 56, 0.25);
      border-radius: 10px;
      color: var(--accent);
      font-size: 14px;
      font-weight: 600;
      margin-top: 20px;
    }
    
    /* Steps */
    .step { 
      margin-bottom: 24px; 
      padding: 24px 28px;
      background: var(--dark-card);
      border-radius: 16px;
      border-left: 4px solid var(--primary);
      border-top: 1px solid rgba(255,255,255,0.04);
      border-right: 1px solid rgba(255,255,255,0.04);
      border-bottom: 1px solid rgba(255,255,255,0.04);
    }
    
    .step-number {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      background: var(--primary);
      border-radius: 8px;
      font-size: 14px;
      font-weight: 700;
      margin-right: 12px;
    }
    
    .step-title { font-size: 18px; font-weight: 700; margin-bottom: 16px; color: var(--text); }
    
    .step-tasks { list-style: none; }
    
    .step-tasks li { 
      padding: 10px 0; 
      padding-left: 28px; 
      position: relative; 
      color: var(--text-muted);
      font-size: 15px;
      border-bottom: 1px solid rgba(255,255,255,0.04);
    }
    
    .step-tasks li:last-child { border-bottom: none; }
    
    .step-tasks li:before { 
      content: "→"; 
      position: absolute; 
      left: 0; 
      color: var(--accent);
      font-weight: bold;
    }
    
    /* Tips */
    .tips { display: flex; flex-direction: column; gap: 12px; }
    
    .tip { 
      padding: 18px 22px;
      background: linear-gradient(90deg, rgba(251, 191, 36, 0.08) 0%, transparent 100%);
      border-left: 4px solid #fbbf24;
      border-radius: 0 14px 14px 0;
    }
    
    .tip-text { color: rgba(255,255,255,0.85); font-size: 15px; line-height: 1.7; }
    
    /* Footer */
    .footer { 
      margin-top: 60px; 
      padding: 30px;
      background: linear-gradient(135deg, rgba(78, 98, 244, 0.1) 0%, rgba(194, 140, 250, 0.05) 100%);
      border-radius: 16px;
      border: 1px solid rgba(78, 98, 244, 0.15);
      text-align: center;
    }
    
    .footer-brand { 
      margin-bottom: 12px;
    }
    
    .footer-brand img {
      height: 40px;
      width: auto;
      object-fit: contain;
    }
    
    .footer-text { color: var(--text-muted); font-size: 14px; margin-top: 8px; }
    
    .footer-url {
      display: inline-block;
      margin-top: 16px;
      padding: 10px 24px;
      background: var(--primary);
      color: white;
      text-decoration: none;
      border-radius: 10px;
      font-weight: 600;
      font-size: 14px;
    }
    
    /* Print styles */
    @media print { 
      body { 
        background: #ffffff !important; 
        color: #1F2937 !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      
      .page {
        background: #ffffff !important;
      }
      
      .header, .overview, .step, .benefit, .asset, .footer, .tip { 
        background: #f3f4f6 !important;
        border-color: #d1d5db !important;
      }
      
      /* Headings and titles - dark blue */
      .strategy-name {
        background: none !important;
        -webkit-text-fill-color: #4E62F4 !important;
        color: #4E62F4 !important;
      }
      
      /* Logo images - ensure visible in print */
      .logo, .footer-brand {
        background: #1a1a2e !important;
        padding: 16px 24px !important;
        border-radius: 12px !important;
        display: inline-block !important;
      }
      
      .logo img, .footer-brand img {
        filter: none !important;
        opacity: 1 !important;
      }
      
      .section-title {
        color: #1F2937 !important;
        border-color: #4E62F4 !important;
      }
      
      .section-title span {
        color: #4E62F4 !important;
      }
      
      /* All text elements - dark gray for readability */
      .strategy-desc,
      .strategy-category,
      .overview-text,
      .benefit span,
      .benefit-icon,
      .step-title,
      .step-tasks li,
      .tip-text,
      .footer-text,
      .timeline,
      .asset-label {
        color: #374151 !important;
        -webkit-text-fill-color: #374151 !important;
      }
      
      /* Numbers and accents - primary blue */
      .asset-number,
      .step-number {
        background: #4E62F4 !important;
        color: #ffffff !important;
        -webkit-text-fill-color: #ffffff !important;
      }
      
      /* Badge */
      .badge {
        background: rgba(78, 98, 244, 0.1) !important;
        border-color: #4E62F4 !important;
        color: #4E62F4 !important;
      }
      
      /* Step tasks arrow */
      .step-tasks li:before {
        color: #4E62F4 !important;
      }
      
      /* Tip left border */
      .tip {
        border-left-color: #f59e0b !important;
      }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="logo"><img src="https://customer-assets.emergentagent.com/job_copy-design-ai/artifacts/ouhen3du_LOGO%20PARA%20FONDO%20OSCURO.png" alt="FASCINAI ONE" /></div>
      <div class="badge">★ Tu Estrategia Recomendada</div>
      <div class="strategy-icon">${result.icon}</div>
      <div class="strategy-category">${result.category}</div>
      <h1 class="strategy-name">${result.strategy}</h1>
      <p class="strategy-desc">${result.description}</p>
    </div>

    <div class="section">
      <h2 class="section-title"><span>✓</span> Por qué esta estrategia es para ti</h2>
      <div class="benefits">
        ${result.benefits.map(b => `<div class="benefit"><div class="benefit-icon">✓</div><span>${b}</span></div>`).join('')}
      </div>
    </div>

    <div class="section">
      <h2 class="section-title"><span>📦</span> Lo que incluye este funnel</h2>
      <div class="assets-grid">
        <div class="asset"><div class="asset-number">${result.assets.landings}</div><div class="asset-label">Landings</div></div>
        <div class="asset"><div class="asset-number">${result.assets.emails}</div><div class="asset-label">Emails</div></div>
        <div class="asset"><div class="asset-number">${result.assets.whatsapp}</div><div class="asset-label">WhatsApp</div></div>
        <div class="asset"><div class="asset-number">${result.assets.creativos}</div><div class="asset-label">Creativos</div></div>
      </div>
    </div>

    ${guide.overview ? `
    <div class="section">
      <h2 class="section-title"><span>📋</span> Visión General</h2>
      <div class="overview">
        <p class="overview-text">${guide.overview}</p>
        ${guide.timeline ? `<div class="timeline">⏱ ${guide.timeline}</div>` : ''}
      </div>
    </div>
    ` : ''}

    ${guide.steps ? `
    <div class="section">
      <h2 class="section-title"><span>🚀</span> Plan de Implementación</h2>
      ${guide.steps.map((step, idx) => `
        <div class="step">
          <h3 class="step-title"><span class="step-number">${idx + 1}</span>${step.title}</h3>
          <ul class="step-tasks">
            ${step.tasks.map(task => `<li>${task}</li>`).join('')}
          </ul>
        </div>
      `).join('')}
    </div>
    ` : ''}

    ${guide.tips ? `
    <div class="section">
      <h2 class="section-title"><span>💡</span> Tips Pro</h2>
      <div class="tips">
        ${guide.tips.map(tip => `<div class="tip"><p class="tip-text">${tip}</p></div>`).join('')}
      </div>
    </div>
    ` : ''}

    <div class="footer">
      <div class="footer-brand"><img src="https://customer-assets.emergentagent.com/job_copy-design-ai/artifacts/ouhen3du_LOGO%20PARA%20FONDO%20OSCURO.png" alt="FASCINAI ONE" /></div>
      <p class="footer-text">Crea todos los assets de tu funnel con IA</p>
      <a href="https://fascinai.pro" class="footer-url">fascinai.pro</a>
    </div>
  </div>
</body>
</html>
    `;

    // Create blob and download
    const blob = new Blob([pdfContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    // Open in new window for printing as PDF
    const printWindow = window.open(url, '_blank');
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  // Hero/Start Screen
  if (!started) {
    return (
      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_copy-design-ai/artifacts/ouhen3du_LOGO%20PARA%20FONDO%20OSCURO.png" 
              alt="FASCINAI ONE" 
              className="h-10 object-contain"
            />
          </div>
        </header>

        {/* Hero Section */}
        <main className="pt-24 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6366F1]/20 border border-[#6366F1]/30 text-[#818CF8] text-sm mb-8">
              <Sparkles className="w-4 h-4" />
              <span>Quiz Gratuito · 2 minutos</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Descubre la <span className="text-[#818CF8]">estrategia perfecta</span> de marketing
              <br />para tu negocio
            </h1>

            {/* Subheadline */}
            <p className="text-xl text-white/60 mb-10 max-w-2xl mx-auto">
              Responde 7 preguntas y te diremos exactamente qué tipo de funnel necesitas 
              según tu producto, audiencia y objetivos.
            </p>

            {/* CTA Button */}
            <button
              onClick={() => setStarted(true)}
              className="group inline-flex items-center gap-3 px-8 py-4 bg-[#6366F1] hover:bg-[#5558E3] text-white text-lg font-semibold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#6366F1]/30"
            >
              <Target className="w-5 h-5" />
              Empezar Quiz
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Trust indicators */}
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/40 text-sm">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#818CF8]" />
                <span>14 estrategias analizadas</span>
              </div>
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-[#818CF8]" />
                <span>40+ variantes de funnel</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#818CF8]" />
                <span>Recomendación personalizada</span>
              </div>
            </div>
          </div>

          {/* Preview Cards */}
          <div className="max-w-5xl mx-auto mt-16 grid md:grid-cols-3 gap-6 px-4">
            {[
              { icon: "🔴", title: "Lanzamiento", desc: "Webinars en vivo con urgencia real" },
              { icon: "🌿", title: "Evergreen", desc: "Funnels automatizados 24/7" },
              { icon: "📞", title: "High Ticket", desc: "Ventas de alto valor con llamada" }
            ].map((item) => (
              <div 
                key={item.title}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-[#6366F1]/50 transition-colors"
              >
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-white/10 py-8">
          <div className="max-w-6xl mx-auto px-4 text-center text-white/40 text-sm">
            <p>© 2024 FASCINAI ONE. Todos los derechos reservados.</p>
          </div>
        </footer>
      </div>
    );
  }

  // Lead Capture Screen (GHL Form)
  if (showLeadCapture) {
    return (
      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_copy-design-ai/artifacts/ouhen3du_LOGO%20PARA%20FONDO%20OSCURO.png" 
              alt="FASCINAI ONE" 
              className="h-10 object-contain"
            />
          </div>
        </header>

        <main className="pt-24 pb-16 px-4">
          <div className="max-w-lg mx-auto">
            {/* Success Badge */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-sm mb-4">
                <Sparkles className="w-4 h-4" />
                <span>¡Quiz completado!</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Tu estrategia está lista
              </h2>
              <p className="text-white/60">
                Déjanos tus datos para enviarte el resultado y recursos exclusivos
              </p>
            </div>

            {/* GHL Form */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/form/x9d9vBXgVTEJWtCCA8h3"
                style={{ width: '100%', height: '400px', border: 'none', borderRadius: '8px' }}
                id="inline-x9d9vBXgVTEJWtCCA8h3"
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Form 37"
                data-height="400"
                data-layout-iframe-id="inline-x9d9vBXgVTEJWtCCA8h3"
                data-form-id="x9d9vBXgVTEJWtCCA8h3"
                title="Form 37"
              />
            </div>

            {/* Skip Button */}
            <div className="text-center">
              <button
                onClick={handleShowResult}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#6366F1] hover:bg-[#5558E3] text-white font-semibold rounded-xl transition-all hover:scale-105"
              >
                Ver mi resultado
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Result Screen
  if (result) {
    return (
      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <img 
              src="https://customer-assets.emergentagent.com/job_copy-design-ai/artifacts/ouhen3du_LOGO%20PARA%20FONDO%20OSCURO.png" 
              alt="FASCINAI ONE" 
              className="h-10 object-contain"
            />
            <button 
              onClick={handleRestart}
              className="text-sm text-white/70 hover:text-white transition-colors"
            >
              Hacer quiz de nuevo
            </button>
          </div>
        </header>

        <main className="pt-24 pb-16 px-4">
          <div className="max-w-3xl mx-auto">
            {/* Result Badge */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 text-green-400 text-sm mb-6">
                <Sparkles className="w-4 h-4" />
                <span>Tu estrategia recomendada</span>
              </div>
            </div>

            {/* Main Result Card */}
            <div className="bg-gradient-to-br from-[#6366F1]/20 to-transparent border border-[#6366F1]/30 rounded-3xl p-8 md:p-12 mb-8">
              <div className="flex items-start gap-6 mb-6">
                <div className="text-6xl">{result.icon}</div>
                <div>
                  <div className="text-[#818CF8] text-sm font-medium mb-1">{result.category}</div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white">{result.strategy}</h1>
                </div>
              </div>
              
              <p className="text-lg text-white/70 mb-8 leading-relaxed">
                {result.description}
              </p>

              {/* Benefits */}
              <div className="space-y-3 mb-8">
                {result.benefits.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-white/80">{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Assets Preview */}
              <div className="bg-black/30 rounded-2xl p-6 mb-8">
                <h3 className="text-sm text-white/50 uppercase tracking-wider mb-4">Lo que incluye este funnel</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#818CF8]">{result.assets.landings}</div>
                    <div className="text-sm text-white/50">Landings</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#818CF8]">{result.assets.emails}</div>
                    <div className="text-sm text-white/50">Emails</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#818CF8]">{result.assets.whatsapp}</div>
                    <div className="text-sm text-white/50">WhatsApp</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-[#818CF8]">{result.assets.creativos}</div>
                    <div className="text-sm text-white/50">Creativos</div>
                  </div>
                </div>
              </div>

              {/* CTA - Download PDF */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleDownloadPDF}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#6366F1] hover:bg-[#5558E3] text-white font-semibold rounded-xl transition-all hover:scale-[1.02]"
                >
                  <Download className="w-5 h-5" />
                  Descargar Guía PDF Completa
                  <FileText className="w-5 h-5" />
                </button>
                <button
                  onClick={handleRestart}
                  className="px-6 py-4 border border-white/20 hover:border-white/40 text-white/70 hover:text-white font-medium rounded-xl transition-all"
                >
                  Repetir quiz
                </button>
              </div>
            </div>

            {/* PDF Preview Info */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#818CF8]" />
                ¿Qué incluye la guía PDF?
              </h3>
              <ul className="space-y-2 text-white/70">
                <li className="flex items-center gap-2">
                  <span className="text-[#818CF8]">→</span>
                  Plan de implementación paso a paso
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#818CF8]">→</span>
                  Timeline recomendado para tu lanzamiento
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#818CF8]">→</span>
                  Checklist de tareas por cada fase
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#818CF8]">→</span>
                  Tips pro de expertos en funnels
                </li>
              </ul>
            </div>

            {/* Additional Info */}
            <div className="text-center text-white/50 text-sm">
              <p>
                La guía se abrirá en una nueva ventana.<br />
                Usa "Guardar como PDF" o "Imprimir" para descargarla.
              </p>
            </div>

            {/* CTA Taller */}
            <div className="mt-10 bg-gradient-to-br from-[#C6FD38]/20 to-[#6366F1]/10 border border-[#C6FD38]/30 rounded-3xl p-8 text-center">
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                ¿Quieres implementar esta estrategia con IA?
              </h3>
              <p className="text-white/60 mb-6 max-w-md mx-auto">
                Únete al taller gratuito del 25 de Junio y aprende a crear todos los assets de tu funnel en minutos con FASCINAI ONE.
              </p>
              <a
                href="https://fascinads.com/registro-fascinai-one/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#C6FD38] hover:bg-[#b8ef2a] text-black font-bold rounded-xl transition-all hover:scale-105"
              >
                Reservar mi plaza gratis
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Quiz Questions Screen
  const question = QUESTIONS[currentQuestion];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_copy-design-ai/artifacts/ouhen3du_LOGO%20PARA%20FONDO%20OSCURO.png" 
            alt="FASCINAI ONE" 
            className="h-10 object-contain"
          />
          <button 
            onClick={handleRestart}
            className="text-sm text-white/70 hover:text-white transition-colors"
          >
            Empezar de nuevo
          </button>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
              <div 
                className="h-full bg-[#6366F1] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-sm text-white/50 text-center">
              Pregunta {currentQuestion + 1} de {QUESTIONS.length}
            </div>
          </div>

          {/* Question */}
          <div className={`transition-all duration-300 ${isAnimating ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}`}>
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">
              {question.title}
            </h2>
            <p className="text-white/50 text-center mb-8">
              {question.subtitle}
            </p>

            {/* Options */}
            <div className="space-y-3">
              {question.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#6366F1]/50 hover:bg-[#6366F1]/10 transition-all group text-left"
                >
                  <div className="text-3xl">{option.icon}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-white group-hover:text-[#818CF8] transition-colors">
                      {option.title}
                    </div>
                    <div className="text-sm text-white/50">{option.desc}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[#818CF8] group-hover:translate-x-1 transition-all" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuizLanding;
